import { Link } from "react-router-dom";
import { Heart, Camera, Car, Fuel, Settings2, Gauge, BadgeCheck, Eye, Share2, Scale } from "lucide-react";
import { formatPrice } from "../lib/api";
import { useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../i18n/I18nContext";
import { useCompare } from "../context/CompareContext";

export default function CarCard({ listing, compact = false }) {
  const { user } = useAuth();
  const { t } = useI18n();
  const { toggle: toggleCompare, isInCompare, items: compareItems, max: maxCompare } = useCompare();
  const [saved, setSaved] = useState(false);
  const isRental = listing.listing_type === "rental";
  const cover = listing.images?.[0] || "https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=940&q=80";
  const photoCount = listing.images?.length || 0;
  const isSold = listing.status === "sold" || listing.status === "rented";
  const inCompare = isInCompare(listing.id);

  const toggleSave = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return;
    try {
      const { data } = await api.post(`/saved/${listing.id}`);
      setSaved(data.saved);
    } catch (err) {
      console.warn("toggleSave failed", err);
    }
  };

  const shareListing = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}/listing/${listing.id}`;
    const title = `${listing.year} ${listing.brand} ${listing.model}`;
    const price = isRental ? `$${listing.daily_price}/day` : (listing.price ? `$${Number(listing.price).toLocaleString()}` : "");
    const text = `${title}${price ? ` — ${price}` : ""} on Car Market`;
    if (navigator.share) {
      try {
        await navigator.share({ url, title, text });
      } catch (err) {
        if (err?.name !== "AbortError") console.warn("share failed", err);
      }
    } else {
      try {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      } catch {
        toast.error("Could not copy link");
      }
    }
  };

  const onToggleCompare = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!inCompare && compareItems.length >= maxCompare) {
      toast.error(`You can compare up to ${maxCompare} cars at a time`);
      return;
    }
    toggleCompare(listing);
    toast.success(inCompare ? "Removed from compare" : "Added to compare");
  };

  const dealerName = listing.dealer?.dealer_name;
  const conditionLabel = listing.condition === "New" ? "Dealer New Car" : (listing.seller_type === "dealer" ? "Dealer Used Car" : "Private Seller");

  // Auto "Just listed" badge — true if created within the last 7 days.
  const isJustListed = (() => {
    if (!listing.created_at) return false;
    try {
      const createdMs = new Date(listing.created_at).getTime();
      return Date.now() - createdMs < 7 * 24 * 60 * 60 * 1000;
    } catch { return false; }
  })();
  const isPriceDrop = Boolean(listing.price_drop); // optional backend flag — off by default

  return (
    <Link
      to={`/listing/${listing.id}`}
      data-testid={`car-card-${listing.id}`}
      className="group block bg-white sm:rounded-2xl border-y sm:border sm:border-slate-200 border-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-0.5 transition-all duration-300"
    >
      {/* Image with photo count badge + save button */}
      <div className="relative aspect-[16/11] overflow-hidden bg-slate-100">
        <img src={cover} alt={`${listing.brand} ${listing.model}`} loading="lazy"
             className={`w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ${isSold ? 'grayscale brightness-90' : ''}`} />

        {/* SOLD / RENTED watermark */}
        {isSold && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="bg-cm-accent text-white font-display font-extrabold text-3xl tracking-[0.3em] px-6 py-2 rounded-md shadow-2xl rotate-[-12deg] border-4 border-white/40">
              {listing.status === "rented" ? "RENTED" : "SOLD"}
            </span>
          </div>
        )}

        {listing.featured && (
          <span className="absolute top-3 left-3 bg-cm-primary text-white text-[10px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full flex items-center gap-1 shadow-md">
            <BadgeCheck className="w-3 h-3" /> Featured
          </span>
        )}
        {!listing.featured && isJustListed && (
          <span className="absolute top-3 left-3 bg-emerald-500 text-white text-[10px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full shadow-md">
            Just Listed
          </span>
        )}
        {isPriceDrop && (
          <span className={`absolute ${(listing.featured || isJustListed) ? 'top-11' : 'top-3'} left-3 bg-cm-accent text-white text-[10px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full shadow-md`}>
            Price Drop
          </span>
        )}
        {isRental && (
          <span className="absolute top-3 left-3 bg-cm-accent text-white text-[10px] font-semibold tracking-wider uppercase px-2.5 py-1 rounded-full shadow-md">
            For Rent
          </span>
        )}

        {/* Save/heart + Share + Compare top-right */}
        <div className="absolute top-3 right-3 flex flex-col gap-2">
          <button
            onClick={toggleSave}
            data-testid={`save-listing-${listing.id}`}
            aria-label="Save listing"
            className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md hover:scale-110 transition-transform"
          >
            <Heart className={`w-[18px] h-[18px] ${saved ? 'fill-cm-accent text-cm-accent' : 'text-cm-primary'}`} />
          </button>
          <button
            onClick={shareListing}
            data-testid={`share-listing-${listing.id}`}
            aria-label="Share listing"
            className="w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md hover:scale-110 transition-transform"
          >
            <Share2 className="w-[17px] h-[17px] text-cm-primary" />
          </button>
          <button
            onClick={onToggleCompare}
            data-testid={`compare-listing-${listing.id}`}
            aria-label={inCompare ? "Remove from compare" : "Add to compare"}
            className={`w-10 h-10 rounded-full backdrop-blur flex items-center justify-center shadow-md hover:scale-110 transition-transform ${inCompare ? 'bg-cm-primary text-white' : 'bg-white/90 text-cm-primary'}`}
          >
            <Scale className="w-[17px] h-[17px]" />
          </button>
        </div>

        {/* Photo count + view count bottom-left */}
        <div className="absolute bottom-3 left-3 flex flex-col gap-1.5">
          {photoCount > 0 && (
            <span className="inline-flex items-center gap-1.5 bg-black/55 text-white text-xs font-semibold px-2.5 py-1 rounded-md backdrop-blur">
              <Camera className="w-3.5 h-3.5" /> {photoCount}
            </span>
          )}
          {listing.views != null && listing.views > 0 && (
            <span className="inline-flex items-center gap-1.5 bg-black/55 text-white text-xs font-semibold px-2.5 py-1 rounded-md backdrop-blur">
              <Eye className="w-3.5 h-3.5" /> {listing.views >= 1000 ? `${(listing.views / 1000).toFixed(1)}k` : listing.views}
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-5">
        <h3 className="font-display font-extrabold text-cm-primary text-[19px] leading-tight line-clamp-1">
          {listing.year} {listing.brand} {listing.model}
        </h3>
        {listing.variant && <p className="text-xs text-slate-500 mt-1">{listing.variant}</p>}

        {/* Price */}
        <div className="mt-3">
          {isRental ? (
            <div className="flex items-baseline gap-1.5">
              <span className="font-display font-extrabold text-2xl text-cm-primary">${listing.daily_price}</span>
              <span className="text-sm text-slate-500">/day</span>
              {listing.monthly_price && (
                <span className="ml-2 text-xs text-slate-500">· ${listing.monthly_price}/mo</span>
              )}
            </div>
          ) : (
            <>
              <div className="font-display font-extrabold text-2xl text-cm-primary leading-none">
                {formatPrice(listing.price)}<span className="text-cm-primary">*</span>
              </div>
              <p className="text-[11px] text-slate-500 mt-1">Excl. Govt. Charges</p>
            </>
          )}
        </div>

        {!compact && (
          <>
            {/* Divider */}
            <div className="border-t border-slate-100 my-4" />

            {/* Spec grid 2×2 with navy icons */}
            <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-sm text-slate-700">
              {listing.body_type && (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-full border border-cm-primary/25 text-cm-primary flex items-center justify-center flex-shrink-0">
                    <Car className="w-3.5 h-3.5" />
                  </span>
                  <span className="truncate">{listing.body_type}</span>
                </div>
              )}
              {listing.fuel_type && (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-full border border-cm-primary/25 text-cm-primary flex items-center justify-center flex-shrink-0">
                    <Fuel className="w-3.5 h-3.5" />
                  </span>
                  <span className="truncate">{listing.fuel_type}</span>
                </div>
              )}
              {listing.transmission && (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-full border border-cm-primary/25 text-cm-primary flex items-center justify-center flex-shrink-0">
                    <Settings2 className="w-3.5 h-3.5" />
                  </span>
                  <span className="truncate">{listing.transmission}</span>
                </div>
              )}
              {listing.mileage != null && (
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-7 h-7 rounded-full border border-cm-primary/25 text-cm-primary flex items-center justify-center flex-shrink-0">
                    <Gauge className="w-3.5 h-3.5" />
                  </span>
                  <span className="truncate">{Number(listing.mileage).toLocaleString()} km</span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-slate-100 mt-4 pt-3" />

            {/* Seller / dealer row */}
            <div className="text-sm">
              <span className="font-semibold text-cm-primary">{conditionLabel}</span>
              {dealerName && (
                <>
                  <span className="text-slate-400 mx-1.5">–</span>
                  <span className="font-semibold text-cm-accent">{dealerName}</span>
                  {listing.dealer?.verified_status && (
                    <BadgeCheck className="inline-block w-3.5 h-3.5 text-emerald-500 ml-1 -mt-0.5" />
                  )}
                </>
              )}
            </div>
            {/* Location */}
            <div className="mt-1 text-xs text-slate-500">
              {listing.location}
              {listing.dealer?.address && (
                <>
                  <span className="text-cm-accent mx-1.5">–</span>
                  <span>{listing.dealer.address.split(",").pop().trim()}</span>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </Link>
  );
}
